/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	TemplateRef,
	contentChild,
	inject,
	input
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { ButtonComponent } from "../button/button.component";
import { CbDialogConfig, CbDialogRef } from "../dialog/dialog.class";
import { IconComponent } from "../icon/icon.component";
import { FeaturedIconComponent } from "../misc-icons/featured-icon/featured-icon.component";

@Component({
	selector: "cb-dialog-layout",

	imports: [
		CommonModule,
		IconComponent,
		ButtonComponent,
		TranslateModule,
		FeaturedIconComponent,
	],
	templateUrl: "./dialog-layout.component.html",
	styleUrls: ["./dialog-layout.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogLayoutComponent {
	public readonly width = input<string>();
	public readonly height = input<string>();
	public readonly title = input.required<string>();
	public readonly subtitle = input<string>();
	public readonly icon = input<string | null>();
	public readonly stickyControls = input(false);
	public readonly iconColor = input<ReturnType<typeof FeaturedIconComponent.prototype.color>>("brand");
	public readonly iconType = input<ReturnType<typeof FeaturedIconComponent.prototype.type>>("modern");
	public readonly hideCloseButton = input(false);
	public readonly hideControls = input(false);

	public readonly dialogRef: CbDialogRef = inject(CbDialogRef);
	public readonly dialogConfig: CbDialogConfig = inject(CbDialogConfig);

	protected readonly headlineControlsTemplate = contentChild("HeadlineControls", { read: TemplateRef });
	protected readonly titleControlsTemplate = contentChild("TitleControls", { read: TemplateRef });
	protected readonly subtitleTemplate = contentChild("Subtitle", { read: TemplateRef });
	protected readonly bodyTemplate = contentChild("Body", { read: TemplateRef });
	protected readonly leftButtonsTemplate = contentChild("LeftButtons", { read: TemplateRef });
	protected readonly rightButtonsTemplate = contentChild("RightButtons", { read: TemplateRef });

	public close(): void {
		this.dialogRef.close();
	}
}
